import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api';
import { queryKeys } from '../queryClient';
import type { AvatarConfig, Hero, InventoryItem } from '../../types';

export function useHeroProfile(heroId: string | undefined) {
  return useQuery({
    queryKey: [
      ...queryKeys.hero(heroId || ''),
      'profile',
    ] as const,
    queryFn: async () => {
      if (!heroId) throw new Error('Hero ID is required');
      const result = await apiGet<{
        hero: import('../../contexts/HeroContext').HeroProfile;
      }>(`/api/heroes/${heroId}`);
      if (!result.ok) throw new Error(result.error.error);
      return result.data.hero;
    },
    enabled: !!heroId,
  });
}

export function useHero(heroId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.hero(heroId || ''),
    queryFn: async () => {
      if (!heroId) throw new Error('Hero ID is required');
      const result = await apiGet<{ hero: Hero }>(
        `/api/heroes/${heroId}`
      );
      if (!result.ok) throw new Error(result.error.error);
      return result.data.hero;
    },
    enabled: !!heroId,
  });
}

export function useAvatar(
  heroId: string | undefined,
  initialConfig?: AvatarConfig
) {
  return useQuery({
    queryKey: queryKeys.heroAvatar(heroId || ''),
    queryFn: async () => {
      if (!heroId) throw new Error('Hero ID is required');
      const result = await apiGet<{
        avatarConfig: AvatarConfig;
      }>(`/api/heroes/${heroId}/avatar`);
      if (!result.ok) throw new Error(result.error.error);
      return result.data.avatarConfig;
    },
    enabled: !!heroId && !initialConfig,
    initialData: initialConfig,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useEquipment(heroId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.heroEquipment(heroId || ''),
    queryFn: async () => {
      if (!heroId) throw new Error('Hero ID is required');
      const result = await apiGet<{
        equipment: InventoryItem[];
      }>(`/api/heroes/${heroId}/equipment`);
      if (!result.ok) throw new Error(result.error.error);
      return result.data.equipment;
    },
    enabled: !!heroId,
  });
}

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const result = await apiGet<{
        user: any;
        hero: Hero;
      }>(`/api/me`);
      if (!result.ok) throw new Error(result.error.error);
      return result.data;
    },
  });
}
